import styled, { css } from 'styled-components';
import type { ColorName } from 'styles/config/colors';
import themeColors from 'styles/config/colors';
import themeSpacing from 'styles/config/spacing';
import type { Text } from 'styles/config/text';
import text from 'styles/config/text';
import rem from 'styles/functions/rem';

import Input from '../Input';
import { CaptionStrong } from '../LegacyText';

const SLIDER_DEFAULT_HEIGHT = rem(4);
const SLIDER_DEFAULT_WIDTH = rem(100);

export const THUMB_WIDTH = 24;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.xs3};
  align-items: flex-start;
  justify-content: space-between;

  width: 100%;
`;

export const SliderRow = styled.div<{
  $disabled?: boolean;
  $sliderWidth?: string;
}>`
  display: flex;
  flex-direction: row;
  gap: ${themeSpacing.xs3};
  align-items: center;

  width: ${({ $sliderWidth }) => $sliderWidth || '100%'};

  > .minLabel {
    flex-shrink: 0;
    min-width: ${themeSpacing.sm4};
    text-align: center;
  }

  > .slider {
    flex: 1;
    width: ${({ $sliderWidth }) => $sliderWidth || '100%'};
  }

  > .maxLabel {
    flex-shrink: 0;
    min-width: ${themeSpacing.sm4};
    text-align: center;
  }

  > .inputField {
    flex-shrink: 0;
    margin-left: ${themeSpacing.xs3};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      color: ${themeColors.textDisabled};
    `};
`;

export const SliderInputWrapper = styled.div<{ $mobileWidth?: boolean }>`
  display: flex;
  gap: ${themeSpacing.xs3};
  align-items: center;

  /* justify-content: flex-start; */

  height: ${themeSpacing.sm3};

  ${({ $mobileWidth }) =>
    $mobileWidth &&
    css`
      width: 100%;
    `};
`;

export const Slider = styled.input<{
  $backgroundColor: ColorName;
  $withTransparentBackground: boolean;
  $thumbBackgroundColor: ColorName;
  $onHoverThumbBackgroundColor: ColorName;
  $value: number;
  $sliderHeight?: string;
  $sliderWidth?: string;
  $tooltipOnHover?: boolean;
}>`
  ${({ $sliderWidth = SLIDER_DEFAULT_WIDTH }) => css`
    width: 100%;
    min-width: ${$sliderWidth};
    max-width: 100%;
  `};
  height: ${({ $sliderHeight = SLIDER_DEFAULT_HEIGHT }) => $sliderHeight};
  margin: 0;
  appearance: none;
  ${({ $value }) => css`
    background: linear-gradient(to right, ${themeColors.sliderTrackActive} ${$value}%, ${themeColors.sliderTrackInactive} 0);
  `}
  border-radius: ${themeSpacing.radiusLarge};

  ${({ $withTransparentBackground }) =>
    $withTransparentBackground &&
    css`
      background: transparent;
    `};

  &::-webkit-slider-thumb {
    cursor: pointer;

    position: relative;

    width: ${THUMB_WIDTH}px;
    height: ${THUMB_WIDTH}px;
    padding: 0;

    appearance: none;
    background: ${themeColors.sliderThumb};
    border: 2px solid ${themeColors.sliderThumbBorder};
    border-radius: ${themeSpacing.radiusFull};
  }

  &::-webkit-slider-thumb:hover {
    background: ${themeColors.sliderThumb};
  }

  &:active + output {
    display: block;
  }

  &:disabled {
    pointer-events: all;
  }

  ${({ $tooltipOnHover }) =>
    $tooltipOnHover &&
    css`
      &:hover + output {
        display: block;
      }
    `}

  ${({ disabled, $value, $withTransparentBackground }) =>
    disabled &&
    css`
      pointer-events: none;

      ${
        !$withTransparentBackground &&
        css`
        background: linear-gradient(
          to right,
          ${themeColors.sliderTrackActive} ${$value}%,
          ${themeColors.sliderTrackInactive} 0
        );
      `
      }

      &::-webkit-slider-thumb {
        background: ${themeColors.sliderThumb};
        border-color: ${themeColors.sliderThumbBorder};
      }
    `}
`;

export const ValueIndicator = styled(Input)`
  width: 54px;
  height: 40px;
  padding: ${themeSpacing.xs2};

  font-weight: ${text.weight.regular};
  text-align: center;
`;

export const LabelWrapper = styled.label<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${({ $disabled }) =>
    $disabled &&
    css`
      color: ${themeColors.textDisabled};
    `}
`;

export const TooltipText = styled(CaptionStrong)`
  text-decoration: underline dashed ${themeColors.textHelper};
  text-underline-offset: ${themeSpacing.xs1};
`;

export const RangeControl = styled.div<{ $mobileWidth?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  ${({ $mobileWidth }) =>
    $mobileWidth &&
    css`
      width: 100%;
    `}
`;

export const Segments = styled.span`
  pointer-events: none;

  position: absolute;

  display: flex;
  gap: ${themeSpacing.xs1};

  width: 100%;
  height: 100%;
`;

export const Segment = styled.span<{
  $isFilled: boolean;
  $fillColor: ColorName;
  $disabled?: boolean;
}>`
  width: 100%;
  height: 100%;
  background-color: ${themeColors.sliderTrackInactive};
  border-radius: ${themeSpacing.radiusXSmall};

  ${({ $isFilled }) =>
    $isFilled &&
    css`
      background-color: ${themeColors.sliderTrackActive};
    `}

  ${({ $disabled, $isFilled }) =>
    $disabled &&
    css`
      background-color: ${
        $isFilled
          ? themeColors.backgroundDisabledStrong
          : themeColors.backgroundDisabledSubtle
      };
    `}
`;

export type SliderTooltipWidth = 'auto' | 'fixed';
export type SliderTooltipPosition = 'left' | 'right' | 'center';

export const SliderTooltip = styled.output<{
  $leftPosition?: string;
  $sliderTooltipWidth?: SliderTooltipWidth;
  $sliderTooltipLineHeight?: keyof Text['lineHeight'];
  $position?: SliderTooltipPosition;
}>`
  position: absolute;
  top: -${themeSpacing.md1};
  left: ${({ $leftPosition }) => $leftPosition};
  transform: ${({ $position = 'center' }) =>
    $position === 'left'
      ? 'translateX(-90%)'
      : $position === 'right'
        ? 'translateX(-10%)'
        : 'translateX(-50%)'};

  display: none;

  width: ${themeSpacing.md1};
  height: auto;

  font-size: ${text.size.body.small};
  line-height: ${({ $sliderTooltipLineHeight = 'loose' }) =>
    text.lineHeight[$sliderTooltipLineHeight]};
  color: ${themeColors.textPrimaryInverse};
  text-align: center;

  background-color: ${themeColors.backgroundInverse};
  border-radius: ${themeSpacing.radiusXSmall};

  ${({ $sliderTooltipWidth = 'fixed' }) =>
    $sliderTooltipWidth === 'auto' &&
    css`
      width: auto;
      padding: ${themeSpacing.xs1} ${themeSpacing.xs3};
    `};

  ::after {
    content: "";

    position: absolute;
    bottom: -3.5px;
    left: ${({ $position = 'center' }) =>
      $position === 'left' ? '90%' : $position === 'right' ? '10%' : '50%'};
    transform: translateX(-50%) rotate(45deg);

    width: 7px;
    height: 7px;

    background: inherit;
  }
`;
